/**
 * Reference-feature renderer for the facility map.
 *
 * Draws a non-storage feature (building, fence, driveway, gate, landmark) as an
 * SVG shape styled by kind. Closed features (buildings, driveways, landmarks)
 * render as filled polygons; open features (fences, gates) render as heavy
 * polylines. Purely presentational — selection/editing is handled by the
 * parent canvas.
 */

import { pointsToSvgString, type Point } from "./shapeUtils";
import type { FeatureKind } from "@/types/yard";

/** Per-kind visual treatment. Colors are literal (SVG) design-system tints. */
const FEATURE_STYLES: Record<
  FeatureKind,
  { fill: string; stroke: string; dash?: string; width?: number }
> = {
  building: { fill: "#94a3b8", stroke: "#475569" },
  driveway: { fill: "#d6d3d1", stroke: "#78716c", dash: "5 3" },
  fence: { fill: "none", stroke: "#57534e", width: 3 },
  gate: { fill: "none", stroke: "#16a34a", width: 3 },
  landmark: { fill: "#fcd34d", stroke: "#d97706" },
  other: { fill: "#cbd5e1", stroke: "#64748b" },
};

interface FeatureShapeProps {
  id: string;
  label: string;
  kind: FeatureKind;
  geometryType: "polygon" | "polyline";
  points: Point[];
  color?: string | null;
  isSelected: boolean;
  scale: number;
  onClick: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
}

function FeatureShape({
  id,
  label,
  kind,
  geometryType,
  points,
  color,
  isSelected,
  scale,
  onClick,
  onContextMenu,
}: FeatureShapeProps) {
  if (!points || points.length < 2) return null;

  const style = FEATURE_STYLES[kind] ?? FEATURE_STYLES.other;
  const stroke = isSelected ? "#7c3aed" : (color ?? style.stroke);
  const baseWidth = style.width ?? 1.5;
  const strokeWidth = (isSelected ? baseWidth + 1 : baseWidth) / scale;

  // Label anchor: centroid for closed shapes, midpoint for open runs.
  const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
  const cy = points.reduce((s, p) => s + p.y, 0) / points.length;
  const fontSize = Math.max(8, 10 / scale);

  return (
    <g
      data-feature-id={id}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onContextMenu={onContextMenu}
      style={{ cursor: "pointer" }}
    >
      {geometryType === "polygon" ? (
        <polygon
          points={pointsToSvgString(points)}
          fill={color ?? style.fill}
          fillOpacity={kind === "landmark" ? 0.5 : 0.35}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={
            style.dash
              ? style.dash
                  .split(" ")
                  .map((n) => Number(n) / scale)
                  .join(" ")
              : undefined
          }
          strokeLinejoin="round"
        />
      ) : (
        <polyline
          points={pointsToSvgString(points)}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* Invisible fat hit-target for thin polylines */}
      {geometryType === "polyline" && (
        <polyline
          points={pointsToSvgString(points)}
          fill="none"
          stroke="transparent"
          strokeWidth={10 / scale}
        />
      )}

      <text
        x={cx}
        y={cy}
        fontSize={fontSize}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="var(--text-secondary, #475569)"
        style={{ paintOrder: "stroke", pointerEvents: "none" }}
        stroke="var(--color-surface-secondary, #f8fafc)"
        strokeWidth={3 / scale}
        strokeLinejoin="round"
      >
        {label}
      </text>
    </g>
  );
}

export default FeatureShape;
